import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { PageHeader, REMOTE_PERMISSIONS, useContainerQuery } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { OperatorContextSwitcher } from '@backrs_ui/grow-ui/src/components/PageShell/OperatorContextSwitcher';
import { useHasPermission } from '@backrs_ui/grow-ui/src/hooks/useHasPermission';
import { useIsInstitutionAdmin } from '@backrs_ui/grow-ui/src/hooks/useIsInstitutionAdmin';
import { usePlatformMode } from '@backrs_ui/grow-ui/src/hooks/usePlatformMode';
import {
  NO_DATA_FILTER_VALUE,
  useGetCoachingAnalyticsAyotQuery,
  useGetCoachingAnalyticsBlueprintQuery,
  useGetCoachingAnalyticsCoachingQuery,
  useGetCoachingAnalyticsEngagementQuery,
  useGetCoachingAnalyticsFilterOptionsQuery,
  useGetCoachingAnalyticsLifeReadinessQuery,
  useGetCoachingAnalyticsOverviewQuery,
  useGetCoachingAnalyticsToolUsageQuery,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';
import { useGetInstitutionCoachesQuery } from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  PLATFORM_OPERATOR_CONTEXT,
  selectStoredOperatorInstitutionId,
} from '@backrs_ui/grow-ui/src/redux/operatorContext';
import { personName } from '@backrs_ui/grow-ui/src/utils/personName';

import { Box, type SxProps, type Theme } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { ANALYTICS_SECTION_ID } from './analytics.anchors';
import { programLabel } from './analytics.labels';
import {
  ALL_OPTION,
  AnalyticsFilterBar,
  type AnalyticsFilterKey,
  type AnalyticsFilterValues,
  DEFAULT_FILTERS,
  type FilterOption,
} from './AnalyticsFilterBar';
import { SectionState } from './AnalyticsSectionState';
import { BlueprintAnalyticsSection } from './BlueprintCards';
import { useAgentContext } from './CoachingAnalyticsPageContainer.agentContext';
import { CoachingSection } from './CoachingCards';
import { EngagementSection } from './EngagementCards';
import { LifeReadinessSection } from './LifeReadinessCards';
import { OnTrackSection } from './OnTrackCards';
import { AnalyticsOverviewGrid } from './OverviewCards';
import { ToolUsageCard } from './ToolUsageCards';

const { coachingAnalytics } = appStrings.modules;
const { filters: filterStrings, successSystem } = coachingAnalytics;

const withAll = (options: readonly FilterOption[]): readonly FilterOption[] => [
  ALL_OPTION,
  ...options,
];

/** In platform mode `All` already covers every institution, so it says so. */
const PLATFORM_ALL_OPTION: FilterOption = {
  value: ALL_OPTION.value,
  label: filterStrings.allInstitutions,
};

/** The way in: choosing it changes the context rather than the narrowing. */
const PLATFORM_ENTRY_OPTION: FilterOption = {
  value: PLATFORM_OPERATOR_CONTEXT,
  label: filterStrings.platformOption,
};

// The plain Student role is every student's default, so it is noise as a
// program filter.
const DEFAULT_PROGRAM = 'student';

export const CoachingAnalyticsPageContainer = () => {
  // Measures the column, not the window - the resizable chat panel takes width out of the page.
  // Below 650 the filter fields need a third row.
  const [containerRef, isCompact] = useContainerQuery(650);
  const [filters, setFilters] = useState<AnalyticsFilterValues>(DEFAULT_FILTERS);

  const operatorInstitutionId = useAppSelector(selectStoredOperatorInstitutionId);
  const [narrowedUnder, setNarrowedUnder] = useState(operatorInstitutionId);

  // Every dimension here names something belonging to the operator root it was
  // chosen under, so none of them survives a change of one. Reset while
  // rendering rather than from an effect: the panels below read `filters` in
  // this same pass, and an effect would let all eight refetch under the old
  // root's narrowing first, which the api refuses.
  if (narrowedUnder !== operatorInstitutionId) {
    setNarrowedUnder(operatorInstitutionId);
    setFilters(DEFAULT_FILTERS);
  }

  const isInstitutionAdmin = useIsInstitutionAdmin();
  // The read is only ever asked for by a caller who could act on its answer, so an admin
  // without the grant issues no request this page did not issue before.
  const canAccessOperatorFleet = useHasPermission(REMOTE_PERMISSIONS.ACCESS_OPERATOR_FLEET);
  const platform = usePlatformMode({ enabled: isInstitutionAdmin && canAccessOperatorFleet });

  const handleFilterChange = (key: AnalyticsFilterKey, value: string) => {
    // The context change resets the narrowings, which is what returns this select to All.
    if (key === 'institution' && value === PLATFORM_OPERATOR_CONTEXT) {
      platform.enter();

      return;
    }

    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Options are re-fetched on every filter change, so each list stays narrowed
  // to what the current selection allows.
  const analyticsQuery = { filter: filters };
  const { data: options, isError: isOptionsError } =
    useGetCoachingAnalyticsFilterOptionsQuery(analyticsQuery);
  const { data: coaches, isError: isCoachesError } = useGetInstitutionCoachesQuery(
    isInstitutionAdmin ? {} : skipToken,
  );

  const overviewQuery = useGetCoachingAnalyticsOverviewQuery(analyticsQuery);
  const blueprintQuery = useGetCoachingAnalyticsBlueprintQuery(analyticsQuery);
  const ayotQuery = useGetCoachingAnalyticsAyotQuery(analyticsQuery);
  const lifeReadinessQuery = useGetCoachingAnalyticsLifeReadinessQuery(analyticsQuery);
  const engagementQuery = useGetCoachingAnalyticsEngagementQuery(analyticsQuery);
  const coachingQuery = useGetCoachingAnalyticsCoachingQuery(
    isInstitutionAdmin ? analyticsQuery : skipToken,
  );
  const toolUsageQuery = useGetCoachingAnalyticsToolUsageQuery(analyticsQuery);

  const institutionOptions = isInstitutionAdmin
    ? [
        platform.active ? PLATFORM_ALL_OPTION : ALL_OPTION,
        ...(options?.institutions ?? []).map((institution) => ({
          value: String(institution.id),
          label: institution.name,
        })),
        ...(platform.available && !platform.active ? [PLATFORM_ENTRY_OPTION] : []),
      ]
    : undefined;

  const programOptions = withAll(
    (options?.programs ?? [])
      .filter((program) => program.value !== DEFAULT_PROGRAM)
      .map((program) => ({
        value: program.value,
        label: filterStrings.withCount(programLabel(program.value), program.count),
      })),
  );

  const gradeOptions = withAll(
    (options?.grades ?? []).map((grade) => ({
      value: grade.value,
      label: filterStrings.withCount(
        grade.value === NO_DATA_FILTER_VALUE ? filterStrings.noGrade : (grade.name ?? grade.value),
        grade.count,
      ),
    })),
  );

  const enrollmentOptions = withAll(
    (options?.enrollment_types ?? []).map((type) => ({
      value: type.value,
      label: filterStrings.withCount(type.name ?? filterStrings.noEnrollmentType, type.count),
    })),
  );

  const coachOptions = isInstitutionAdmin
    ? withAll(
        (coaches ?? []).map((coach) => ({
          value: String(coach.id),
          label: personName(coach),
        })),
      )
    : undefined;

  const { hash } = useLocation();
  // Sections render as their queries resolve, so scrolling to a hash anchor is
  // only reliable once everything above it has laid out.
  const sectionsReady = [
    overviewQuery,
    blueprintQuery,
    ayotQuery,
    lifeReadinessQuery,
    engagementQuery,
    coachingQuery,
    toolUsageQuery,
  ].every((query) => !query.isLoading);

  // Changing a filter re-runs every query, so sectionsReady flips again; without this the
  // page would scroll back to the anchor on each change for as long as the hash is in the URL.
  const scrolledHashRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hash || !sectionsReady || scrolledHashRef.current === hash) return;

    scrolledHashRef.current = hash;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash, sectionsReady]);

  useAgentContext();

  return (
    <Box sx={pageSx} ref={containerRef}>
      <OperatorContextSwitcher id="coaching-analytics-operator-context" />
      <AnalyticsFilterBar
        isCompact={isCompact}
        isOptionsUnavailable={isOptionsError || isCoachesError}
        filters={filters}
        onFilterChange={handleFilterChange}
        institutionOptions={institutionOptions}
        programOptions={programOptions}
        gradeOptions={gradeOptions}
        enrollmentOptions={enrollmentOptions}
        coachOptions={coachOptions}
      />
      <Box id={ANALYTICS_SECTION_ID.overview} sx={anchorSx}>
        <SectionState query={overviewQuery}>
          {(overview) => <AnalyticsOverviewGrid overview={overview} />}
        </SectionState>
      </Box>
      <PageHeader
        title={successSystem.title}
        subtitle={successSystem.subtitle}
        maxWidth={null}
        rightComponents={[
          <LubavButton
            key="discuss-lubav"
            id="coaching-analytics-discuss-lubav"
            label={successSystem.discussWithLubav}
            promptButtonId="coaching_analytics_success_system"
          />,
        ]}
      />
      <SectionState query={blueprintQuery}>
        {(blueprint) => <BlueprintAnalyticsSection data={blueprint} />}
      </SectionState>
      <SectionState query={ayotQuery}>{(ayot) => <OnTrackSection data={ayot} />}</SectionState>
      <SectionState query={lifeReadinessQuery}>
        {(lifeReadiness) => <LifeReadinessSection data={lifeReadiness} />}
      </SectionState>
      <Box id={ANALYTICS_SECTION_ID.engagement} sx={anchorSx}>
        <SectionState query={engagementQuery}>
          {(engagement) => (
            <EngagementSection
              data={engagement}
              toolUsage={
                <SectionState query={toolUsageQuery}>
                  {(toolUsage) => <ToolUsageCard data={toolUsage} />}
                </SectionState>
              }
            />
          )}
        </SectionState>
      </Box>
      {isInstitutionAdmin && (
        <Box id={ANALYTICS_SECTION_ID.coaching} sx={anchorSx}>
          <SectionState query={coachingQuery}>
            {(coaching) => <CoachingSection data={coaching} />}
          </SectionState>
        </Box>
      )}
    </Box>
  );
};

const anchorSx: SxProps<Theme> = ({ backrs }) => ({
  scrollMarginTop: backrs.spacing['spacing-2xl'],
});

const pageSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  // shell content wrapper already adds spacing-2xl (20px) on top, so 4px here yields 24px
  paddingTop: backrs.spacing['spacing-xs'],
  paddingBottom: backrs.spacing['spacing-3xl'],
  width: '100%',
});
